/* Safe in‑memory + (optional) localStorage token store */

// True when window.localStorage is a real, functional Storage.
// The `getItem` check is required because Node 22+ ships a stub
// `globalThis.localStorage = {}` (no methods) under experimental webstorage,
// which jsdom surfaces as `window.localStorage` in tests.
const hasUsableLocalStorage = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.localStorage !== 'undefined' &&
  typeof window.localStorage.getItem === 'function';

// Browsers may throw on access (Safari private mode, blocked cookies, quota).
const safeGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    /* no-op: read/write disabled by browser policy */
  }
};

export const makeTokenStorage = (storageKey: string) => {
  type Listener = (t: string | null) => void;

  const isBrowser = hasUsableLocalStorage();

  let token: string | null = isBrowser ? safeGet(storageKey) : null;

  const listeners = new Set<Listener>();
  const notify = () => listeners.forEach(l => l(token));

  if (isBrowser) {
    window.addEventListener('storage', e => {
      if (e.key === storageKey) {
        token = e.newValue;
        notify();
      }
    });
  }

  return {
    get: () => token,
    set: (t: string | null) => {
      token = t;
      if (isBrowser) {
        safeSet(storageKey, t);
      }
      notify();
    },
    subscribe: (l: Listener) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
};

/* Two token stores */
export const accessTokenStore = makeTokenStorage('hw-api-access-token');
export const phpTokenStore = makeTokenStorage('php-api-token');

/* Convenience setters */
export const setAccessToken = accessTokenStore.set;
export const setPhpToken = phpTokenStore.set;
